import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { ResumeDatabaseBannerLoadable } from 'components/Banners/ResumeDatabaseBannerLoadable';
import { SalarySurveyBannerLoadable } from 'components/Banners/SalarySurveyBannerLoadable';
import { ClientOnly } from 'containers/ClientOnly';
import { useShouldPromoteSalarySurvey } from 'modules/salaries/hooks/useShouldPromoteSalarySurvey';
import { useUserEnvironment } from 'store/hooks/useUserEnvironment';
import {
  NotificationStatus,
  ProductNotification,
  useLocalNotifications,
} from 'utils/localNotifications';
import { promoteResumeDatabase } from 'utils/resumeDatabase';
import { setAdType, useAdStore } from 'zustand-stores/layoutStore';

import { BannerAdsBox } from './BannerAds.styled';

export function BannerAds() {
  const { adType } = useAdStore();

  const userEnvironment = useUserEnvironment();
  const { isUserLoaded, isEnvironmentLoaded } = userEnvironment;
  const location = useLocation();

  const { localNotifications, isReady, isSet, updateLocalNotification } =
    useLocalNotifications();

  const salarySurveyNotification = useMemo(
    () =>
      localNotifications?.find(
        (notification) =>
          notification.type === ProductNotification.SALARY_SURVEY_CTA,
      ),

    [localNotifications],
  );

  const resumeDatabaseNotification = useMemo(
    () =>
      localNotifications?.find(
        (notification) =>
          notification.type === ProductNotification.UPLOAD_RESUME,
      ),

    [localNotifications],
  );

  // Set initial status for the notifications
  useEffect(() => {
    if (isReady && !isSet(ProductNotification.UPLOAD_RESUME)) {
      updateLocalNotification(ProductNotification.UPLOAD_RESUME, {
        status: NotificationStatus.NEW,
      });
    }

    if (isReady && !isSet(ProductNotification.SALARY_SURVEY_CTA)) {
      updateLocalNotification(ProductNotification.SALARY_SURVEY_CTA, {
        status: NotificationStatus.NEW,
      });
    }
  }, [isReady, localNotifications, isSet, updateLocalNotification]);

  const showSalarySurveyAd = useShouldPromoteSalarySurvey();

  const showSalarySurveyBanner =
    !location.pathname.includes('/nonprofit-salary-explorer') &&
    showSalarySurveyAd &&
    salarySurveyNotification?.status === NotificationStatus.NEW;

  useEffect(() => {
    if (
      isUserLoaded &&
      isEnvironmentLoaded &&
      adType !== 'SALARY_SURVEY' &&
      showSalarySurveyBanner
    ) {
      setAdType('SALARY_SURVEY');
    }
  }, [isUserLoaded, isEnvironmentLoaded, adType, showSalarySurveyBanner]);

  const showResumeDatabaseBanner =
    !showSalarySurveyBanner &&
    // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
    !['GRAD_FAIRS', 'SALARY_SURVEY'].includes(adType) &&
    promoteResumeDatabase({
      userEnvironment,
      allowLoggedOut: true,
    }) &&
    resumeDatabaseNotification?.status === NotificationStatus.NEW;

  useEffect(() => {
    if (
      isUserLoaded &&
      isEnvironmentLoaded &&
      adType !== 'RESUME_DB' &&
      showResumeDatabaseBanner
    ) {
      setAdType('RESUME_DB');
    }
  }, [isUserLoaded, isEnvironmentLoaded, adType, showResumeDatabaseBanner]);

  return (
    <BannerAdsBox
      data-qa-id="ads-container"
      mt={[60, null, null, 0]}
      mb={[-60, null, null, 0]}
    >
      <ClientOnly>
        {showResumeDatabaseBanner ? <ResumeDatabaseBannerLoadable /> : null}
        {showSalarySurveyBanner ? <SalarySurveyBannerLoadable /> : null}
      </ClientOnly>
    </BannerAdsBox>
  );
}
